/* eslint-disable @angular-eslint/template/label-has-associated-control */

import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { WidgetApiService } from 'libs/rx-form-builder/src/lib/rx-form-presenter/services/widget-api.service';

export interface AllergyData {
  diagnoseDate: string;
  allergy: string;
  widgetApiService?: WidgetApiService;
}

@Component({
  selector: 'app-add-allergy-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    NgSelectModule,
  ],
  template: `
    <h3>Add Allergy</h3>
    <div class="dialog-form">
      <div class="form-field">
        <label>Diagnose Date</label>
        <input
          (focus)="picker.open()"
          [matDatepicker]="picker"
          [(ngModel)]="data.diagnoseDate"
          placeholder="MM/DD/YYYY"
        />
        <mat-datepicker #picker></mat-datepicker>
      </div>
      <div class="form-field">
        <label>Select Allergy</label>
        <ng-select
          [(ngModel)]="data.allergy"
          [items]="allergies"
          bindLabel="label"
          bindValue="value"
          placeholder="Select Allergy"
          [searchable]="true"
          [clearable]="true"
          [addTag]="addTagFn"
          addTagText="Add item"
          [loading]="loading"
          appendTo="body"
          class="ng-select-custom"
        >
          <ng-template ng-option-tmp let-item="item">
            <div class="option-item">
              <span class="option-category" *ngIf="item.category">{{ item.category }}</span>
              <span class="option-label">{{ item.label }}</span>
            </div>
          </ng-template>
        </ng-select>
      </div>
    </div>
    <div class="dialog-actions">
      <button (click)="onCancel()">Cancel</button>
      <button class="blue" (click)="onAdd()" [disabled]="!isValid()">Add</button>
    </div>
  `,
  styles: [
    `
      :host {
        h3 {
          padding: 20px 0 10px;
          margin: 0;
          font-size: 16.5px;
          font-weight: 400;
          color: #000;
        }

        .dialog-form {
          display: flex;
          flex-direction: column;
          gap: 16px;
          max-width: 100%;
          padding: 15px 0;
        }

        .dialog-actions {
          display: flex;
          flex-direction: row;
          justify-content: flex-end;
          gap: 10px;
          padding: 15px 0;

          button {
            font-size: 13.5px;
            font-weight: 400;
            background: transparent;
            color: #000;
            border: solid 1px #ccc;
            padding: 9px 15px;
            border-radius: 5px;
            outline: none;
            cursor: pointer;
            min-width: 75px;

            &.blue {
              background: #1962f3;
              border-color: #1962f3;
              color: white;
            }

            &:disabled {
              opacity: 0.5;
              cursor: not-allowed;
            }
          }
        }

        .form-field {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .form-field label {
          font-size: 14px;
          color: rgba(0, 0, 0, 0.6);
          font-weight: 400;
        }

        .form-field input {
          border-radius: 5px;
          border: solid 1px #ccc;
          padding: 8px;
        }

        .option-item {
          display: flex;
          gap: 12px;
          align-items: center;
        }

        .option-category {
          font-size: 12px;
          color: #999;
          min-width: 90px;
        }

        .option-label {
          flex: 1;
        }
      }

      ::ng-deep {
        .ng-dropdown-panel.ng-select-custom {
          min-height: 56px;
          // transform: translate(1px, -21px);
          padding: 0;
          margin: 0;
          box-shadow: none;
          border-radius: 0 !important;
        }
      }
    `,
  ],
})
export class AddAllergyDialogComponent implements OnInit {
  allergies = [];
  loading = true;

  constructor(
    public dialogRef: MatDialogRef<AddAllergyDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AllergyData,
  ) {
    if (!data.diagnoseDate) {
      data.diagnoseDate = '';
    }
    if (!data.allergy) {
      data.allergy = '';
    }
  }

  ngOnInit() {
    // Load allergies from API if service is provided
    if (this.data.widgetApiService) {
      this.data.widgetApiService.getAllergies().subscribe({
        next: (allergies) => {
          // Map the API response to the format needed for ng-select
          this.allergies = allergies.map((allergy: any) => ({
            category: '', // No category in API response
            value: allergy.AllergyId || '',
            label: allergy.AllergyName || '',
          }));
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading allergies:', error);
          // Fallback to empty or default list
          this.allergies = [];
          this.loading = false;
        },
      });
    } else {
      this.loading = false;
    }
  }

  isValid(): boolean {
    return typeof this.data.allergy === 'string'
      ? this.data.allergy.trim().length > 0
      : !!this.data.allergy;
  }

  addTagFn = (term: string) => {
    return term;
  };

  onCancel(): void {
    this.dialogRef.close();
  }

  onAdd(): void {
    if (this.isValid()) {
      // Find the selected item to get both ID and name
      const selectedItem = this.allergies.find((item) => item.value === this.data.allergy);
      if (selectedItem) {
        // Store the name for display
        this.data.allergy = selectedItem.label;
      }
      this.dialogRef.close(this.data);
    }
  }
}
